/**
 * TranslationRunChip.tsx — PLAT-11690
 *
 * The on-page trace of a translation running in the background, the twin of
 * `DubRunChip`. `TranslationGenerateModal` hands the request to
 * `TranslationStore.startRun`, so pressing Generate can close the popover
 * without orphaning the run; this chip carries it from there.
 *
 * It replaces the old "Translating…" state, which REPLACED the whole language
 * toggle — leaving the reader unable to switch language, open the menu or open
 * a review until the run finished, and never saying which language was running.
 * PLAT-11570 had already rejected exactly that for dubs ("a chip beside the
 * toggle, not in place of it"); this brings translation to the same rule.
 */
import { RunChip } from '@src/components/transcript/RunChip';
import { useStore, useTranslationStore } from '@src/stores-v2/StoreContext';
import { canonicalizeBcp47 } from '@src/utility/languageCaption';
import { observer } from 'mobx-react-lite';

import { useTranslationRunProgress } from './useTranslationRunProgress';

export const TranslationRunChip = observer(function TranslationRunChip() {
  const rootStore = useStore();
  const translationStore = useTranslationStore();
  const activeRun = translationStore?.activeRun ?? null;
  // Per-story, for the same reason the dub chip is: the store outlives a story
  // switch, so without the guard story B's header would claim to be translating
  // and its ✕ would cancel story A's run.
  const run =
    activeRun !== null && activeRun.storyId === rootStore.storyId
      ? activeRun
      : null;
  const progress = useTranslationRunProgress(
    run !== null,
    run ?? { etaMs: 0, startedAt: 0 },
  );
  if (!run) return null;

  const percent = Math.round(progress);

  return (
    <RunChip
      percent={percent}
      code={canonicalizeBcp47(run.targetLanguage)}
      ariaLabel={`Translating to ${run.languageCaption}, ${percent}%`}
      tooltip={`Translating to ${run.languageCaption} — ${percent}%`}
      cancelLabel="Cancel translation"
      cancelTooltip="Cancel Translation"
      onCancel={() => translationStore.cancelRun()}
    />
  );
});
